/**
 * Edits the PO template editor makes (z8nrz7f7dz). Pure and immutable — each
 * returns a new layout — so they are tested here and the editor stays a thin
 * shell over them.
 */

import type { PoCustomFieldDef, PoTemplateLayout } from '@/types/purchaseOrders'

/** `arr` with the item at `index` moved by `delta` places; out of range is a no-op. */
export function moveItem<T>(arr: readonly T[], index: number, delta: number): T[] {
  const to = index + delta
  if (index < 0 || index >= arr.length || to < 0 || to >= arr.length) return [...arr]
  const next = [...arr]
  const [item] = next.splice(index, 1)
  next.splice(to, 0, item)
  return next
}

/** Add an `attr:<key>` column for a material attribute, at the end. Already there: unchanged. */
export function addAttributeColumn(layout: PoTemplateLayout, fieldKey: string, label: string): PoTemplateLayout {
  const key = `attr:${fieldKey}` as const
  if (layout.columns.some((c) => c.key === key)) return layout
  return { ...layout, columns: [...layout.columns, { key, label, visible: true }] }
}

/** A field key from a label: `Cost centre` → `costCentre`. */
export function keyFromLabel(label: string): string {
  const words = label
    .normalize('NFKD')
    // Drop the accents NFKD split off, so "Référence" stays one word.
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\w\s]/g, ' ')
    .trim()
    .split(/[\s_]+/)
    .filter(Boolean)
  const key = words
    .map((w, i) => (i === 0 ? w.toLowerCase() : w[0].toUpperCase() + w.slice(1).toLowerCase()))
    .join('')
  return /^[a-z]/.test(key) ? key : key ? `f${key}` : ''
}

/** What is wrong with the custom fields, or null. Keys are what orders store values under. */
export function customFieldsProblem(fields: PoCustomFieldDef[]): string | null {
  const seen = new Set<string>()
  for (const f of fields) {
    if (!f.label.trim()) return 'Every custom field needs a label.'
    // The backend's PO_CUSTOM_FIELD_KEY_PATTERN: the key is a JSON key on every order.
    if (!/^[a-z][a-zA-Z0-9_]{0,39}$/.test(f.key)) {
      return `“${f.label}”: the key must start with a lowercase letter and use only letters, digits and _ (40 at most).`
    }
    if (seen.has(f.key)) return `Two custom fields share the key “${f.key}”.`
    seen.add(f.key)
    if (f.type === 'select' && !(f.options ?? []).some((o) => o.trim())) return `“${f.label}”: a select needs at least one option.`
  }
  return null
}

/** Whether two layouts differ — the editor's dirty flag. */
/** JSON with object keys sorted: the server stores layouts as JSONB, which does not keep key order. */
export function canonicalJson(value: unknown): string {
  return JSON.stringify(value, (_k, v: unknown) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.fromEntries(Object.entries(v as Record<string, unknown>).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)))
      : v,
  )
}

/**
 * Whether two layouts differ in content. Key order is ignored: a layout read
 * back from the server has its keys reordered by JSONB, and must not read as
 * an unsaved change.
 */
export function layoutChanged(a: PoTemplateLayout | null | undefined, b: PoTemplateLayout | null | undefined): boolean {
  return canonicalJson(a ?? null) !== canonicalJson(b ?? null)
}
